import {
  AdverseMediaEntityListApi,
  AlertFilterResultApi,
  BridgeAlertApi,
  Configuration,
  DashboardApi,
  EventsApi,
  MappingApi,
  MfaApi,
  Middleware,
  OauthApi,
  OrganisationApi,
  PermissionApi,
  PersonScreeningContextConfigurationApi,
  QueryTemplateApi,
  QuickStatusCommentApi,
  ResponseContext,
  ResponseError,
  RiskRuleApi,
  RoleApi,
  SanctionAlertsApi,
  SanctionAlertsBulkApi,
  SanctionHitApi,
  SanctionListsApi,
  SanctionsApi,
  ScenarioApi,
  ScreeningAlertFilterApi,
  ScreeningAlertFilterResultsApi,
  ScreeningClearanceRuleTestingApi,
  ScreeningTestApi,
  ScreeningTestCaseApi,
  TransactionSummaryApi,
  UserApi,
  UserEventApi,
  WebhookApi,
  WebhookRequestApi,
} from './api';
import store from './store/store';
import { showFeedback } from './feedback/feedbackReducer';
import {
  AlertAssignmentApi,
  AlertEventInternalApi,
  AlertFilterApi,
  AlertFilterResultInternalApi,
  AlertNoteTemplateInternalApi,
  AlertRfiApi,
  AlertRfiConfigInternalApi,
  AlertRfiMessageApi,
  AlertStatusApi,
  BatchInternalApi,
  BridgeAttachmentApi,
  BridgeConversationApi,
  BridgeConversationCategoryApi,
  BridgeConversationConclusionApi,
  BridgeOrganisationApi,
  BridgeSettingsApi,
  BridgeTemplateApi,
  BulkAlertStatusApi,
  CaseAlertInternalApi,
  CaseAssignedStatusInternalApi,
  CaseAssignmentInternalApi,
  CaseDocumentInternalApi,
  CaseInternalApi,
  CaseNoteInternalApi,
  CasePersonInternalApi,
  CaseRationaleInternalApi,
  CaseStatusInternalApi,
  CaseTransactionInternalApi,
  Configuration as InternalApiConfiguration,
  CurrentUserApi,
  CustomListApi,
  CustomListRecordInternalApi,
  CustomListSchemaApi,
  CustomListSignificantFieldApi,
  CustomListUsableFieldApi,
  DecisionRulesApi,
  DocumentInternalApi,
  DowJonesStateApi,
  EntityGroupsApi,
  EntityGroupScreeningConfigurationApi,
  FinalRiskApi,
  InternalRiskApi,
  InternalRiskRuleApi,
  InternalScreeningFlowDraftsApi,
  InternalScreeningFlowsApi,
  InternalScreeningTestsApi,
  JMESPathExpressionTestApi,
  ManualAlertInternalApi,
  MonitoringAlertInternalApi,
  NoteDraftInternalApi,
  NoteInternalApi,
  OrganisationCloneInternalApi,
  PersonInternalApi,
  PersonScreenableValuesCalculationApi,
  PersonSegmentResultsApi,
  PersonSnoozeApi,
  PersonStatusInternalApi,
  RateLimitApi,
  SanctionAlertDocumentInternalApi,
  ScenarioInternalApi,
  ScenarioLibraryInternalApi,
  ScenarioMetricsApi,
  ScenarioTestingApi,
  ScenarioVersionInternalApi,
  SchemaApi,
  ScreeningAlertStatusesApi,
  ScreeningClearanceRuleApi,
  ScreeningClearanceRuleContentApi,
  ScreeningClearanceRulesAnalysisApi,
  ScreeningEventApi,
  ScreeningSearchInternalApi,
  ScreeningSelectorsApi,
  SearchIndexApi,
  TransactionInternalApi,
  TransactionSegmentResultsApi,
  TransactionSummaryV2Api,
  TransliterationApi,
  UniversalDocumentsApi,
  UserAlertRfiMessageNotificationApi,
  UserBasicInternalApi,
  EngineeringOperationsApi,
  UserForgotPasswordApi,
  ReviewRequestsApi,
} from './api-internal';
import { getStoredToken, logoutAndDeleteToken } from './auth/authReducer';
import { refreshAuthentication } from './auth/authActions';
import ErrorIgnoreSentry from './tracking/ErrorIgnoreSentry';
import SessionExpiredError from './tracking/SessionExpiredError';
import { parseErrorMessage } from './common/errors/helpers';

declare module '@tanstack/react-query' {
  interface Register {
    defaultError: Response;
  }
}

export const DEFAULT_RETRY_COUNT = 3;

const basePath = `//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}/api`;

const internalApiBasePath = `//${window.location.hostname}${
  window.location.port ? `:${window.location.port}` : ''
}/internal`;

export const errorMessage = (context: ResponseContext) => `${context.response.status} ${context.url.split('?')[0]}`;

export const apiErrorLoggerMiddleware: Middleware = {
  async post(context: ResponseContext): Promise<Response | void> {
    if (!context.response.ok) {
      const status = context.response.status;

      if (status === 401) {
        store.dispatch(logoutAndDeleteToken());
        store.dispatch(
          showFeedback({
            message: 'Session expired, please authorize',
            success: false,
          })
        );
        throw new SessionExpiredError('Session expired, please authorize');
      }

      if (status !== 404) {
        const message = await parseErrorMessage(context.response.clone());
        store.dispatch(showFeedback({ message, success: false }));
      }

      const reportedToSentryCodes = [400]; // 4xx that should be reported to Sentry

      // throw here for Sentry to ignore those
      if (status < 500 && !reportedToSentryCodes.includes(status)) {
        throw new ErrorIgnoreSentry(status, await parseErrorMessage(context.response));
      }
      throw new ResponseError(context.response, errorMessage(context));
    }
  },
  async onError(): Promise<Response | void> {
    store.dispatch(
      showFeedback({
        message: 'Network error, please check your internet connection',
        success: false,
      })
    );

    throw new ErrorIgnoreSentry();
  },
};

class TokenLoader {
  private static readonly TOKEN_REFRESH_LOCK = 'token-refresh';
  private static readonly TOKEN_EXPIRY_THRESHOLD_SECONDS = 60 * 5;

  async loadToken(): Promise<string> {
    const tokenInfo = getStoredToken();
    if (tokenInfo && tokenInfo.expiresIn < TokenLoader.TOKEN_EXPIRY_THRESHOLD_SECONDS) {
      try {
        await navigator.locks.request(TokenLoader.TOKEN_REFRESH_LOCK, async () => {
          const freshTokenInfo = getStoredToken();
          if (freshTokenInfo && freshTokenInfo.expiresIn < TokenLoader.TOKEN_EXPIRY_THRESHOLD_SECONDS) {
            await refreshAuthentication(store.dispatch, freshTokenInfo);
          }
        });
      } catch (e: any) {
        const currentToken = getStoredToken();
        if (!currentToken || currentToken.expiresIn <= 0) {
          store.dispatch(showFeedback({ message: await parseErrorMessage(e) }));
          store.dispatch(logoutAndDeleteToken());
          return '';
        }
      }
      return `Bearer ${getStoredToken()?.accessToken}`;
    }
    return `Bearer ${tokenInfo?.accessToken}`;
  }
}

const tokenLoader = new TokenLoader();

export const configuration = new Configuration({
  basePath,
  accessToken: async () => await tokenLoader.loadToken(),
  middleware: [apiErrorLoggerMiddleware],
});

export const internalApiConfiguration = new InternalApiConfiguration({
  basePath: internalApiBasePath,
  accessToken: async () => await tokenLoader.loadToken(),
  middleware: [apiErrorLoggerMiddleware],
});

export const internalApiConfigurationNoFeedback = new InternalApiConfiguration({
  basePath: internalApiBasePath,
  accessToken: async () => await tokenLoader.loadToken(),
  middleware: [],
});

export const userApi = new UserApi(configuration);
export const userBasicInternalApi = new UserBasicInternalApi(internalApiConfiguration);
export const currentUserApi = new CurrentUserApi(internalApiConfiguration);
export const organisationApi = new OrganisationApi(configuration);
export const permissionApi = new PermissionApi(configuration);
export const roleApi = new RoleApi(configuration);
export const scenarioApi = new ScenarioApi(configuration);
export const scenarioInternalApi = new ScenarioInternalApi(internalApiConfiguration);
export const scenarioMetricsApi = new ScenarioMetricsApi(internalApiConfiguration);
export const scenarioLibraryInternalApi = new ScenarioLibraryInternalApi(internalApiConfiguration);
export const scenarioVersionInternalApi = new ScenarioVersionInternalApi(internalApiConfiguration);
export const scenarioTestingApi = new ScenarioTestingApi(internalApiConfiguration);
export const personInternalApi = new PersonInternalApi(internalApiConfiguration);
export const batchApi = new BatchInternalApi(internalApiConfiguration);
export const schemaApi = new SchemaApi(internalApiConfiguration);
export const transactionsSummaryApi = new TransactionSummaryApi(configuration);
export const transactionsSummaryV2Api = new TransactionSummaryV2Api(internalApiConfiguration);
export const finalRiskApi = new FinalRiskApi(internalApiConfiguration);
export const riskApi = new InternalRiskApi(internalApiConfiguration);
export const riskRuleApi = new RiskRuleApi(configuration);
export const mfaApi = new MfaApi(configuration);
export const dashboardApi = new DashboardApi(configuration);
export const mappingsApi = new MappingApi(configuration);
export const querytemplateApi = new QueryTemplateApi(configuration);
export const webhookApi = new WebhookApi(configuration);
export const webhookRequestApi = new WebhookRequestApi(configuration);
export const sanctionListApi = new SanctionListsApi(configuration);
export const adverseMediaEntityListApi = new AdverseMediaEntityListApi(configuration);
export const sanctionAlertsApi = new SanctionAlertsApi(configuration);
export const sanctionHitApi = new SanctionHitApi(configuration);
export const personScreeningContextConfigurationApi = new PersonScreeningContextConfigurationApi(configuration);
export const searchIndexApi = new SearchIndexApi(internalApiConfiguration);
export const eventsApi = new EventsApi(configuration);
export const sanctionsApi = new SanctionsApi(configuration);
export const sanctionAlertsBulkApi = new SanctionAlertsBulkApi(configuration);
export const oauthApi = new OauthApi(configuration);
export const screeningSearchApi = new ScreeningSearchInternalApi(internalApiConfiguration);
export const screeningClearanceRuleApi = new ScreeningClearanceRuleApi(internalApiConfiguration);
export const screeningClearanceRulesAnalysisApi = new ScreeningClearanceRulesAnalysisApi(internalApiConfiguration);
export const screeningClearanceRuleContentApi = new ScreeningClearanceRuleContentApi(internalApiConfiguration);
export const screeningClearanceRulesTestingApi = new ScreeningClearanceRuleTestingApi(configuration);
export const quickStatusCommentApi = new QuickStatusCommentApi(configuration);
export const screeningTestApi = new ScreeningTestApi(configuration);
export const screeningTestCaseApi = new ScreeningTestCaseApi(configuration);
export const transliterationApi = new TransliterationApi(internalApiConfiguration);
export const transactionApi = new TransactionInternalApi(internalApiConfiguration);
export const transactionInternalApi = new TransactionInternalApi(internalApiConfiguration);
export const screeningEventApi = new ScreeningEventApi(internalApiConfiguration);
export const userEventApi = new UserEventApi(configuration);
export const screeningAlertFilterApi = new ScreeningAlertFilterApi(configuration);
export const screeningAlertFilterResultsApi = new ScreeningAlertFilterResultsApi(configuration);
export const alertFilterResultApi = new AlertFilterResultApi(configuration);
export const screeningAlertStatusesApi = new ScreeningAlertStatusesApi(internalApiConfiguration);
export const decisionRulesApi = new DecisionRulesApi(internalApiConfiguration);
export const dowJonesStateApi = new DowJonesStateApi(internalApiConfiguration);

export const conversationApi = new BridgeConversationApi(internalApiConfiguration);
export const conversationCategoryApi = new BridgeConversationCategoryApi(internalApiConfiguration);
export const conversationConclusionApi = new BridgeConversationConclusionApi(internalApiConfiguration);

export const bridgeTemplateApi = new BridgeTemplateApi(internalApiConfiguration);

export const userAlertRfiMessageNotificationApi = new UserAlertRfiMessageNotificationApi(internalApiConfiguration);
export const attachmentApi = new BridgeAttachmentApi(internalApiConfiguration);
export const bridgeOrganisationApi = new BridgeOrganisationApi(internalApiConfiguration);
export const organisationCloneInternalApi = new OrganisationCloneInternalApi(internalApiConfiguration);
export const alertRfiApi = new AlertRfiApi(internalApiConfiguration);
export const customListApi = new CustomListApi(internalApiConfiguration);

export const customListUsableFieldApi = new CustomListUsableFieldApi(internalApiConfiguration);
export const customListSchemaApi = new CustomListSchemaApi(internalApiConfiguration);
export const customListRecordApi = new CustomListRecordInternalApi(internalApiConfiguration);

export const alertRfiMessageApi = new AlertRfiMessageApi(internalApiConfiguration);

export const alertStatusApi = new AlertStatusApi(internalApiConfiguration);

export const bridgeSettingsApi = new BridgeSettingsApi(internalApiConfiguration);

export const jmespathExpressionTestApi = new JMESPathExpressionTestApi(internalApiConfiguration);

export const alertFilterApi = new AlertFilterApi(internalApiConfiguration);

export const alertAssignmentInternalApi = new AlertAssignmentApi(internalApiConfiguration);

export const alertNoteTemplateInternalApi = new AlertNoteTemplateInternalApi(internalApiConfiguration);

export const snoozeInternalApi = new PersonSnoozeApi(internalApiConfiguration);

export const bridgeAlertApi = new BridgeAlertApi(configuration);

export const bulkCloseApi = new BulkAlertStatusApi(internalApiConfiguration);

export const monitoringAlertInternalApi = new MonitoringAlertInternalApi(internalApiConfiguration);
export const manualAlertInternalApi = new ManualAlertInternalApi(internalApiConfiguration);
export const userForgotPasswordApi = new UserForgotPasswordApi(internalApiConfigurationNoFeedback);

export const alertFilterInternalApi = new AlertFilterResultInternalApi(internalApiConfiguration);

export const personStatusInternalApi = new PersonStatusInternalApi(internalApiConfiguration);

export const customListSignificantFieldApi = new CustomListSignificantFieldApi(internalApiConfiguration);
export const documentInternalApi = new DocumentInternalApi(internalApiConfiguration);
export const sanctionAlertDocumentInternalApi = new SanctionAlertDocumentInternalApi(internalApiConfiguration);
export const noteInternalApi = new NoteInternalApi(internalApiConfiguration);
export const noteDraftInternalApi = new NoteDraftInternalApi(internalApiConfiguration);
export const universalDocumentsApi = new UniversalDocumentsApi(internalApiConfiguration);
export const alertEventsApi = new AlertEventInternalApi(internalApiConfiguration);
export const alertRfiConfig = new AlertRfiConfigInternalApi(internalApiConfiguration);
export const rateLimitApi = new RateLimitApi(internalApiConfiguration);
export const segmentsApi = new EntityGroupsApi(internalApiConfiguration);
export const personSegmentResultsApi = new PersonSegmentResultsApi(internalApiConfiguration);
export const transactionSegmentResultsApi = new TransactionSegmentResultsApi(internalApiConfiguration);
export const segmentScreeningConfigurationApi = new EntityGroupScreeningConfigurationApi(internalApiConfiguration);
export const screeningSelectorsApi = new ScreeningSelectorsApi(internalApiConfiguration);
export const screeningFlowsApi = new InternalScreeningFlowsApi(internalApiConfiguration);
export const screeningFlowDraftsApi = new InternalScreeningFlowDraftsApi(internalApiConfiguration);
export const screeningTestsApi = new InternalScreeningTestsApi(internalApiConfiguration);
export const personScreenableValuesCalculationApi = new PersonScreenableValuesCalculationApi(internalApiConfiguration);
export const internalRiskRuleApi = new InternalRiskRuleApi(internalApiConfiguration);
export const caseInternalApi = new CaseInternalApi(internalApiConfiguration);
export const caseAlertInternalApi = new CaseAlertInternalApi(internalApiConfiguration);
export const caseDocumentInternalApi = new CaseDocumentInternalApi(internalApiConfiguration);
export const caseNoteInternalApi = new CaseNoteInternalApi(internalApiConfiguration);
export const caseTransactionInternalApi = new CaseTransactionInternalApi(internalApiConfiguration);
export const casePersonInternalApi = new CasePersonInternalApi(internalApiConfiguration);
export const caseRationaleInternalApi = new CaseRationaleInternalApi(internalApiConfiguration);
export const caseStatusInternalApi = new CaseStatusInternalApi(internalApiConfiguration);
export const caseAssignmentInternalApi = new CaseAssignmentInternalApi(internalApiConfiguration);
export const caseAssignedStatusInternalApi = new CaseAssignedStatusInternalApi(internalApiConfiguration);
export const reviewRequestsInternalApi = new ReviewRequestsApi(internalApiConfiguration);

export const engineeringOperationsApi = new EngineeringOperationsApi(internalApiConfiguration);

export * from './api';
